import { type Component, createEffect, createResource, Suspense } from 'solid-js';
import { useParams } from '@solidjs/router';
import { createStore } from 'solid-js/store';
import { apiFetch, twclsx, useToast, useBreadCrumbs, Input } from '../shared';
import { type MiscConfigs } from '../globals';
import Loading from '../shared/Loading';

interface Props {
  class?: string;
}

const defaultConfigs = {
  invoice_number_prefix: '',
};

const Misc: Component<Props> = props => {
  const { apiPromise } = useToast()!;
  const params = useParams();
  const [configs, setConfigs] = createStore<Partial<MiscConfigs>>(defaultConfigs);
  const { setBreadCrumbs } = useBreadCrumbs()!;

  const configFetch = async (entityId: string) => {
    const configs = await apiPromise(
      apiFetch(`/entities/${entityId}/configs`, {
        credentials: 'include',
      }),
      'Failed to fetch balance sheet',
    );

    if (configs) {
      return configs;
    }

    return defaultConfigs;
  };

  const [fetchedConfig] = createResource<MiscConfigs, string>(params.entityId, configFetch);

  setBreadCrumbs([
    {
      label: 'Configs',
    },
    {
      label: 'Miscellaneous',
      href: `/entities/${params.entityId}/configs/misc`,
    },
  ]);

  createEffect(() => {
    if (fetchedConfig.state === 'ready') {
      setConfigs(() => fetchedConfig());
    }
  });

  const onSubmit = async (event: Event) => {
    event.preventDefault();

    await apiPromise(
      apiFetch(`/entities/${params.entityId}/configs`, {
        credentials: 'include',
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          invoice_number_prefix: configs.invoice_number_prefix,
        }),
      }),
      'Failed to update configurations',
      'Configurations updated successfully',
    );
  };

  return (
    <>
      <div class={twclsx('basis-1', 'grow', 'relative', 'justify-center', 'justify-items-center', 'pt-11')}>
        <div
          class={twclsx(
            'p-4',
            'bg-bgprimary',
            'block',
            'sm:flex',
            'items-center',
            'justify-between',
            'border-b',
            'border-gray-200',
            'dark:bg-gray-800',
            'dark:border-gray-700',
          )}
        >
          <div class={twclsx('w-full')}>
            <div class={twclsx('')}>
              <h1 class={twclsx('text-xl', 'font-semibold', 'text-gray-900', 'sm:text-2xl', 'dark:text-white')}>
                Miscellaneous Configurations
              </h1>
            </div>
          </div>
        </div>
        <div
          class={twclsx(
            'p-4',
            'bg-bgprimary',
            'border-b',
            'border-gray-200',
            'dark:bg-gray-800',
            'dark:border-gray-700',
          )}
        >
          <form class={twclsx('grid', 'gap-4')} onSubmit={onSubmit}>
            <Suspense fallback={<Loading />}>
              <Input
                type="text"
                id="invoice_number_prefix"
                label="Invoice Number Prefix"
                name="invoice_number_prefix"
                placeholder="Invoice Number Prefix"
                class={twclsx('rounded-lg', 'text-sm', 'p-1', 'dark:bg-gray-700')}
                value={configs.invoice_number_prefix}
                onInput={(e: Event & { target: HTMLInputElement }) => {
                  setConfigs('invoice_number_prefix', e.target.value);
                }}
              />

              <button
                type="submit"
                class={twclsx(
                  'col-span-12 sm:col-span-3',
                  'mx-auto',
                  'text-white',
                  'bg-blue-700',
                  'w-full',
                  'rounded-lg',
                  'p-2',
                )}
              >
                Submit
              </button>
            </Suspense>
          </form>
        </div>
      </div>
    </>
  );
};

export default Misc;
